import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Card, CardContent, Chip, Container, FormControl, Grid2,
  InputLabel, MenuItem, Select, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import ModernNavbar from '../setup/ModernNavbar';
import Footer from '../setup/Footer';
import '../../services/GlobalVariables';

const API = `${global.SET_API_URL}monthlyOffPolicies`;
const blankForm = {
  policyID: '', policyName: '', applyLevel: 'COMPANY', referenceID: '',
  offType: 'SUNDAY', allowedDays: 0, effectiveFrom: new Date().toISOString().slice(0, 10),
  effectiveTo: '', status: 'active', remarks: ''
};
const levelLabels = { COMPANY: 'Company', UNIT: 'Unit', DEPARTMENT: 'Department', DESIGNATION: 'Designation', EMPLOYEE: 'Employee' };
const typeLabels = { SUNDAY: 'All Sundays', SATURDAY_SUNDAY: 'Saturday & Sunday', FLEXIBLE_MONTHLY: 'Flexible Monthly Offs', NO_OFF: 'No Off' };

export default function MonthlyOffPolicy() {
  const [form, setForm] = useState(blankForm);
  const [policies, setPolicies] = useState([]);
  const [lookups, setLookups] = useState({ units: [], departments: [], designations: [], employees: [] });
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState({ open: false, severity: 'success', message: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [policyResponse, lookupResponse] = await Promise.all([axios.get(API), axios.get(`${API}/lookups`)]);
      setPolicies(policyResponse.data || []);
      setLookups(lookupResponse.data || { units: [], departments: [], designations: [], employees: [] });
    } catch (error) {
      setNotice({ open: true, severity: 'error', message: error.response?.data?.error || error.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const referenceOptions = useMemo(() => {
    if (form.applyLevel === 'UNIT') return lookups.units || [];
    if (form.applyLevel === 'DEPARTMENT') return lookups.departments || [];
    if (form.applyLevel === 'DESIGNATION') return lookups.designations || [];
    if (form.applyLevel === 'EMPLOYEE') return lookups.employees || [];
    return [];
  }, [form.applyLevel, lookups]);

  const setField = (name) => (event) => {
    const value = event.target.value;
    setForm((old) => ({ ...old, [name]: value, ...(name === 'applyLevel' ? { referenceID: '' } : {}) }));
  };

  const save = async () => {
    setLoading(true);
    try {
      if (form.policyID) await axios.put(`${API}/${form.policyID}`, form);
      else await axios.post(API, form);
      setNotice({ open: true, severity: 'success', message: form.policyID ? 'Policy updated' : 'Policy created' });
      setForm(blankForm);
      await load();
    } catch (error) {
      setNotice({ open: true, severity: 'error', message: error.response?.data?.error || error.message });
    } finally { setLoading(false); }
  };

  const edit = (policy) => setForm({
    ...blankForm, ...policy, referenceID: policy.referenceID || '',
    effectiveFrom: String(policy.effectiveFrom).slice(0, 10),
    effectiveTo: policy.effectiveTo ? String(policy.effectiveTo).slice(0, 10) : ''
  });

  const deactivate = async (policyID) => {
    if (!window.confirm('Deactivate this monthly off policy?')) return;
    try { await axios.delete(`${API}/${policyID}`); await load(); }
    catch (error) { setNotice({ open: true, severity: 'error', message: error.response?.data?.error || error.message }); }
  };

  return <>
    <ModernNavbar />
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <Typography variant="h4" fontWeight={700} mb={2}>Monthly Off Policy Master</Typography>
    <Alert severity="info" sx={{ mb: 2 }}>Priority: Employee → Designation → Department → Company. The highest matching active policy applies.</Alert>
    <Card sx={{ mb: 3 }}><CardContent>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, md: 4 }}><TextField fullWidth size="small" label="Policy Name" value={form.policyName} onChange={setField('policyName')} /></Grid2>
        <Grid2 size={{ xs: 12, md: 2 }}><FormControl fullWidth size="small"><InputLabel>Apply Level</InputLabel><Select label="Apply Level" value={form.applyLevel} onChange={setField('applyLevel')}>{Object.entries(levelLabels).map(([v,l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}</Select></FormControl></Grid2>
        {form.applyLevel !== 'COMPANY' && <Grid2 size={{ xs: 12, md: 3 }}><FormControl fullWidth size="small"><InputLabel>Apply To</InputLabel><Select label="Apply To" value={form.referenceID} onChange={setField('referenceID')}>{referenceOptions.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}</Select></FormControl></Grid2>}
        <Grid2 size={{ xs: 12, md: 3 }}><FormControl fullWidth size="small"><InputLabel>Off Type</InputLabel><Select label="Off Type" value={form.offType} onChange={setField('offType')}>{Object.entries(typeLabels).map(([v,l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}</Select></FormControl></Grid2>
        {form.offType === 'FLEXIBLE_MONTHLY' && <Grid2 size={{ xs: 12, md: 2 }}><TextField fullWidth size="small" type="number" label="Allowed Days" value={form.allowedDays} inputProps={{ min: 0, max: 31 }} onChange={setField('allowedDays')} /></Grid2>}
        <Grid2 size={{ xs: 12, md: 2 }}><TextField fullWidth size="small" type="date" label="Effective From" InputLabelProps={{ shrink: true }} value={form.effectiveFrom} onChange={setField('effectiveFrom')} /></Grid2>
        <Grid2 size={{ xs: 12, md: 2 }}><TextField fullWidth size="small" type="date" label="Effective To" InputLabelProps={{ shrink: true }} value={form.effectiveTo} onChange={setField('effectiveTo')} /></Grid2>
        <Grid2 size={{ xs: 12, md: 2 }}><FormControl fullWidth size="small"><InputLabel>Status</InputLabel><Select label="Status" value={form.status} onChange={setField('status')}><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem></Select></FormControl></Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}><TextField fullWidth size="small" label="Remarks" value={form.remarks || ''} onChange={setField('remarks')} /></Grid2>
        <Grid2 size={{ xs: 12, md: 2 }}><Button fullWidth variant="contained" disabled={loading} onClick={save}>{form.policyID ? 'Update' : 'Save'}</Button></Grid2>
        {form.policyID && <Grid2 size={{ xs: 12, md: 2 }}><Button fullWidth variant="outlined" onClick={() => setForm(blankForm)}>Cancel</Button></Grid2>}
      </Grid2>
    </CardContent></Card>
    <TableContainer component={Card}><Table size="small"><TableHead><TableRow sx={{ bgcolor: 'primary.main' }}>
      {['Policy','Level','Applied To','Off Type','Allowed','Effective Period','Status','Actions'].map((h) => <TableCell key={h} sx={{ color: 'white', fontWeight: 700 }}>{h}</TableCell>)}
    </TableRow></TableHead><TableBody>{policies.map((p) => <TableRow key={p.policyID} hover>
      <TableCell>{p.policyName}</TableCell><TableCell>{levelLabels[p.applyLevel]}</TableCell><TableCell>{p.referenceName}</TableCell>
      <TableCell>{typeLabels[p.offType]}</TableCell><TableCell>{p.offType === 'FLEXIBLE_MONTHLY' ? p.allowedDays : 'Calendar'}</TableCell>
      <TableCell>{String(p.effectiveFrom).slice(0,10)} – {p.effectiveTo ? String(p.effectiveTo).slice(0,10) : 'Open'}</TableCell>
      <TableCell><Chip size="small" color={p.status === 'active' ? 'success' : 'default'} label={p.status} /></TableCell>
      <TableCell><Box display="flex" gap={1}><Button size="small" onClick={() => edit(p)}>Edit</Button><Button size="small" color="error" onClick={() => deactivate(p.policyID)}>Deactivate</Button></Box></TableCell>
    </TableRow>)}</TableBody></Table></TableContainer>
    <Snackbar open={notice.open} autoHideDuration={5000} onClose={() => setNotice((n) => ({ ...n, open: false }))}><Alert severity={notice.severity}>{notice.message}</Alert></Snackbar>
    </Container>
    <Footer />
  </>;
}
